/**
 * Health Recommendations API Service
 * Connects to backend health recommendations endpoints
 */

import apiClient from "./apiClient";

export interface HealthAuthority {
  id: string;
  name: string;
  country: string;
  url: string;
  priority: "primary" | "secondary" | "supplementary";
  credibilityScore: number;
}

export interface HealthRecommendation {
  id: string;
  source: HealthAuthority;
  category:
    | "vaccination"
    | "medication"
    | "precaution"
    | "advisory"
    | "warning";
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  details?: string;
  applicableCountries: string[];
  validFrom: Date;
  validUntil?: Date;
  lastUpdated: Date;
  evidenceLevel: "mandatory" | "recommended" | "suggested" | "optional";
  relatedDiseases?: string[];
  targetAudience?: string[];
}

export interface VaccinationRequirement {
  vaccineName: string;
  isoCode: string;
  isRequired: boolean;
  isRecommended: boolean;
  minDaysBeforeDeparture?: number;
  validityDays?: number;
  boosterRequired?: boolean;
  ageRestrictions?: {
    minAge?: number;
    maxAge?: number;
  };
  contraindications?: string[];
  source: HealthAuthority;
}

export interface DiseaseOutbreak {
  id: string;
  disease: string;
  country: string;
  region?: string;
  severity: "critical" | "high" | "medium" | "low";
  casesReported: number;
  lastReportDate: Date;
  trendDirection: "increasing" | "stable" | "decreasing";
  source: HealthAuthority;
  affectsTravel: boolean;
}

export interface AggregatedHealthAdvice {
  profile: {
    destinationCountry: string;
    destinationRegion?: string;
    travelerNationality: string;
    departureDate: Date;
    returnDate: Date;
    travelType: "cruise" | "flight" | "land" | "mixed";
  };
  riskLevel: "low" | "moderate" | "high" | "critical";
  recommendations: HealthRecommendation[];
  requiredVaccinations: VaccinationRequirement[];
  recommendedVaccinations: VaccinationRequirement[];
  activeOutbreaks: DiseaseOutbreak[];
  lastUpdated: Date;
  sources: HealthAuthority[];
}

/**
 * Get health recommendations for a specific destination
 */
export const getDestinationHealthRecommendations = async (
  countryCode: string,
  params?: {
    departureDate?: string;
    returnDate?: string;
    travelType?: string;
  },
): Promise<AggregatedHealthAdvice> => {
  const response = await apiClient.get(
    `/health-recommendations/destination/${countryCode}`,
    {
      params,
    },
  );
  return response.data.data;
};

/**
 * Get health recommendations for cruise travel
 */
export const getCruiseHealthRecommendations = async (data: {
  ports: string[];
  departureDate: string;
  returnDate?: string;
}): Promise<AggregatedHealthAdvice> => {
  const response = await apiClient.post("/health-recommendations/cruise", data);
  return response.data.data;
};

/**
 * Get list of all health authorities
 */
export const getHealthAuthorities = async (params?: {
  nationality?: string;
  destination?: string;
}): Promise<HealthAuthority[]> => {
  const response = await apiClient.get("/health-recommendations/authorities", {
    params,
  });
  return response.data.data;
};

/**
 * Get cache statistics
 */
export const getHealthRecommendationsStats = async () => {
  const response = await apiClient.get("/health-recommendations/stats/cache");
  return response.data.data;
};
